import React, { useState, useEffect, useContext } from "react";
import { Redirect } from "react-router-dom";
import { Grid, Button, Typography, Box } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import PersonIcon from "@material-ui/icons/Person";
import PersonAddIcon from "@material-ui/icons/PersonAdd";

import translate from "../i18n/Translator";
import Surface from "../components/Surface";
import ValidatedInput, { InputRef } from "../components/ValidatedInput";
import { ErrorSnackbar } from "../components/Snackbars";
import { User, UserRequest, MetadataResponse } from "../model/User";
import { Customer } from "../model/Customer";
import { Tenant } from "../model/Tenant";
import { createUser as createTenantUser, getUser as getTenantUser, updateUser as updateTenantUser, getMetadata as getTenantMetadata } from "../api/TenantUserAPI";
import { createUser as createCustomerUser, getUser as getCustomerUser, updateUser as updateCustomerUser, getMetadata as getCustomerMetadata } from "../api/CustomerUserAPI";
import { createUser, getUser, updateUser, getMetadata } from "../api/UserAPI";
import Progress from "../components/Progress";
import { AppContext } from "../context/AppContext";
import { RouterParams } from "../router/RouterParams";
import { PhoneValidator, phoneFormat } from "../components/Validators";

export default function UserForm({ match }: RouterParams) {
    const context = useContext(AppContext);
    const theme = useTheme();
    const isXs = useMediaQuery(() => theme.breakpoints.down("xs"));
    const isEdit = !!match.params.userId;
    const margin = "dense";
    const phoneValidator = new PhoneValidator();
    const [status, setStatus] = useState<string>("loading");
    const [request, setRequest] = useState<UserRequest>({
        "first_name": "",
        "last_name": "",
        "email": "",
        "role_id": "",
        "phone": ""
    } as UserRequest);
    const [validations, setValidations] = useState({
        "first_name": {
            valid: false
        } as InputRef,
        "last_name": {
            valid: false
        } as InputRef,
        "email": {
            valid: false
        } as InputRef,
        "phone": {
            valid: false
        } as InputRef,
    } as any);
    const [roleIds, setRoleIds] = useState<string[]>([]);
    const [roleNames, setRoleNames] = useState<string[]>([]);
    const [tenant, setTenant] = useState<Tenant>();
    const [error, setError] = useState<string>();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [redirect, setRedirect] = useState<string>();
    const [customer, setCustomer] = useState<Customer>();
    const [format, setFormat] = useState("");

    const retrievePromise = (): Promise<User> => {
        let tenantId = (context.session?.tenant?.id ?? match.params.tenantId);
        if (tenantId) {
            if (match.params.customerId) {
                return getCustomerUser(tenantId, match.params.customerId, match.params.userId, true);
            }
            return getTenantUser(tenantId, match.params.userId, true);
        }
        return getUser(match.params.userId, true);
    }

    const metadataPromise = (): Promise<MetadataResponse> => {
        let tenantId = (context.session?.tenant?.id ?? match.params.tenantId);
        if (tenantId) {
            if (match.params.customerId) {
                return getCustomerMetadata(tenantId, match.params.customerId);
            }
            return getTenantMetadata(tenantId);
        }
        return getMetadata();
    }

    const submitPromise = (): Promise<User> => {
        let tenantId = (context.session?.tenant?.id ?? match.params.tenantId);
        if (isEdit) {
            if (tenantId) {
                if (match.params.customerId) {
                    return updateCustomerUser(tenantId, match.params.customerId, match.params.userId, request);
                }
                return updateTenantUser(tenantId, match.params.userId, request);
            }
            return updateUser(match.params.userId, request);
        }

        if (tenantId) {
            if (match.params.customerId) {
                return createCustomerUser(tenantId, match.params.customerId, request);
            }
            return createTenantUser(tenantId, request);
        }
        return createUser(request)
    };

    useEffect(() => {
        if (isEdit && context.session?.user.id === match.params.userId) {
            setRedirect("/profile");
            return;
        }

        setStatus("loading");
        if (isEdit) {
            retrievePromise().then((user) => {
                setMetadata(user.metadata);
                setRequest({
                    "first_name": user.first_name,
                    "last_name": user.last_name,
                    "email": user.email,
                    "role_id": user.role?.id,
                    "phone": user.phone,
                } as UserRequest);

                setStatus("loaded");
            }).catch((error) => {
                setStatus(error.message);
            });
        } else {
            metadataPromise().then((metadata) => {
                setMetadata(metadata);
                setStatus("loaded");
            }).catch((error) => {
                setStatus(error.message);
            });
        }
        // eslint-disable-next-line
    }, [isEdit, match.params.userId]);

    const setMetadata = (metadata?: MetadataResponse) => {
        if (!metadata) return;

        let filtered = metadata.roles.filter((role) => (match.params.customerId && role.customer) || !role.customer);
        setRoleIds(filtered.map((role) => role.id));
        setRoleNames(filtered.map((role) => translate(`users.roles.${role.id}`) as string));
        setTenant(metadata.tenant);
        setCustomer(metadata.customer);
    }

    const hasChanged = (name: string, value: string, inputRef: InputRef) => {
        setRequest({ ...request, [name]: value });
        validations[name] = inputRef;
        setValidations(validations);
    };

    const hasChangedPhone = (name: string, value: string, inputRef: InputRef) => {
        hasChanged(name, value, inputRef);
        setFormat(phoneFormat(value));
    };

    const isValid = () => {
        var valid = true;
        for (let field in validations) {
            let ref = validations[field];
            if (!ref.valid) {
                ref.blurer(true);
                valid = false;
            }
        }
        return valid;
    }

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isValid()) return;

        setSubmitting(true);
        submitPromise().then((response) => {
            if (match.params.tenantId) {
                setRedirect(`/tenants/${match.params.tenantId}/users`);
            } else if (match.params.customerId) {
                setRedirect(`/customers/${match.params.customerId}/users`);
            } else {
                setRedirect("/users");
            }
        }).catch((error) => {
            setSubmitting(false);
            setError(error.message);
        });
    };

    const onClosedSnackbar = () => {
        setError(undefined);
    };

    if (redirect) {
        return <Redirect to={redirect} />
    }

    if (status === "loading") {
        return <Progress />;
    }

    if (status !== "loaded") {
        return (
            <Typography variant="body1" component="h5" color="error" align="center">
                {status}
            </Typography>
        );
    }

    return (
        <Grid item xs={12}>
            <Grid container justify="center" alignItems="center">
                <Grid item xs={12} md={10} lg={7} xl={5}>
                    <Surface backButton
                        title={translate(isEdit ? "users.edit_user" : "users.new_user")}
                        subtitle={tenant ? tenant.name : (customer ? customer.name : undefined)}
                        icon={isEdit ? <PersonIcon /> : <PersonAddIcon />}
                        className="FormSurface">
                        <form autoComplete="off" noValidate onSubmit={onSubmit}>
                            <Grid container justify="space-between" alignItems="center">
                                <Grid item xs={12}>
                                    <ValidatedInput type="text" id="first_name" name="first_name"
                                        value={request.first_name} label={translate("users.first_name") as string}
                                        required disabled={submitting}
                                        margin={margin}
                                        onValueChanged={hasChanged} />
                                </Grid>
                                <Grid item xs={12}>
                                    <ValidatedInput type="text" id="last_name" name="last_name"
                                        value={request.last_name} label={translate("users.last_name") as string}
                                        required disabled={submitting}
                                        margin={margin}
                                        onValueChanged={hasChanged} />
                                </Grid>
                                <Grid item xs={12}>
                                    <ValidatedInput type="email" id="email" name="email"
                                        value={request.email} label={translate("users.email") as string}
                                        required disabled={submitting}
                                        margin={margin}
                                        onValueChanged={hasChanged} />
                                </Grid>
                                <Grid item xs={12}>
                                    <ValidatedInput type="text" id="phone" name="phone"
                                        value={request.phone} label={translate("users.phone") as string}
                                        disabled={submitting}
                                        validator={phoneValidator}
                                        margin={margin}
                                        format={format} mask=" "
                                        onValueChanged={hasChangedPhone} />
                                </Grid>
                                <Grid item xs={12}>
                                    <ValidatedInput type="text" id="role_id" name="role_id" label={translate("users.roles.title") as string}
                                        value={request.role_id} onValueChanged={hasChanged}
                                        required disabled={submitting}
                                        margin={margin}
                                        options={roleIds} optionLabels={roleNames} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box pt={2}>
                                        <Grid container justify="flex-start" spacing={1} direction="row-reverse">
                                            <Grid item xs sm="auto">
                                                <Button type="submit" variant="contained" color="primary" size="large" disabled={submitting} fullWidth={isXs}>
                                                    {translate(isEdit ? "buttons.update" : "buttons.add")}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                        <ErrorSnackbar message={error} onClose={onClosedSnackbar} />
                    </Surface>
                </Grid>
            </Grid>
        </Grid>
    );
}